import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { normalizeEmail } from '../utils/normalizeEmail';

const API_URL = process.env.REACT_APP_API_URL ?? '';
const RESEND_INTERVAL = 60;
const TRIAL_REQUEST_EMAIL_NOTE =
  '「nslab.co.jp」ドメインからのメールを受信できるように設定してください。\nメールが届かない場合、迷惑メールフォルダの確認もお願いいたします。';
const TRIAL_PLAN_DETAILS = [
  'お試しプランは7日間ご利用になれます。',
  '7日経過後も既にアップロードされたデータはそのまま残りますが、新規データのアップロードはできなくなります。',
  'そのまま継続してご利用されたい場合は、有料サブスクリプション契約が必要です。',
];

const TrialRequest: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!submittedEmail || resendCooldown <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [submittedEmail, resendCooldown]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setInfo('');
    setSubmittedEmail('');

    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail) {
      setError('メールアドレスを入力してください。');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/trial-request`, { email: normalizedEmail });
      setSubmittedEmail(normalizedEmail);
      setEmail('');
      setResendCooldown(RESEND_INTERVAL);
    } catch (err: any) {
      if (err.response?.data?.detail === 'email_exists') {
        setError('このメールアドレスは既に使用されています。');
      } else {
        setError('お試しプランの申込に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!submittedEmail) {
      return;
    }

    setError('');
    setInfo('');
    setIsResending(true);

    try {
      await axios.post(`${API_URL}/api/auth/trial-request/resend`, {
        email: submittedEmail,
      });
      setInfo('お試しプランのご案内メールを再送しました。メールをご確認ください。');
      setResendCooldown(RESEND_INTERVAL);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (detail === 'verification_recently_sent') {
        setError('ご案内メールはすでに送信されています。しばらく経ってから再度お試しください。');
      } else if (detail === 'verification_expired') {
        setError('ログイン期限が切れました。再度お試しプランをお申し込みください。');
      } else {
        setError('お試しプラン案内メールの再送に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          お試しプラン申込
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          align="center"
          sx={{ mb: 3, whiteSpace: 'pre-line' }}
        >
          メールアドレスのみでお申し込みいただけます。{'\n'}初期パスワードをメールで送信します。
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            お試しプランについて
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {TRIAL_PLAN_DETAILS.map((detail) => (
              <Box component="li" key={detail} sx={{ mb: 0.5 }}>
                <Typography variant="body2">{detail}</Typography>
              </Box>
            ))}
          </Box>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {info}
          </Alert>
        )}

        {submittedEmail && (
          <>
            <Alert severity="success" sx={{ mb: 1 }}>
              <Box sx={{ whiteSpace: 'pre-line' }}>
                {'お試しプランのご案内を受け付けました。\n'}
                {`ご登録のメールアドレス(${submittedEmail})宛にご案内を送信しました。`}
              </Box>
            </Alert>
            <Typography
              align="left"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, whiteSpace: 'pre-line' }}
            >
              {TRIAL_REQUEST_EMAIL_NOTE}
            </Typography>
            <Button
              fullWidth
              variant="text"
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
            >
              {isResending ? (
                <CircularProgress size={20} />
              ) : resendCooldown > 0 ? (
                `再送信可能になるまで ${resendCooldown} 秒`
              ) : (
                'ご案内メールを再送する'
              )}
            </Button>
          </>
        )}

        {!submittedEmail && (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="メールアドレス"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'お試しプランを申し込む'
              )}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                ログイン画面に戻る
              </Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default TrialRequest;
